'use client';

import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, WSS_URL } from '../../utils/constants';
import auth from '../../Auth';
import PromptManager from './PromptManager';
import JsonEditorViewer from './JsonEditorViewer';
import { Calendar, Loader2 } from 'lucide-react';
import Breadcrumbs from '../Breadcrumbs';
import { Button } from '@nextui-org/react';
import { apiFetchModels } from '../../utils/api';
import ReactMarkdown from 'react-markdown';
import { Loader, X, StopCircle, Trash } from 'lucide-react';
import { toast } from 'react-toastify';
import { TOAST_STYLE_ERROR, TOAST_STYLE_SUCCESS } from '../../utils/constants';

const TestPrompt = () => {
  const [prompt, setPrompt] = useState({
    prompt_id: '',
    max_tokens: null,
    reserved_tokens_budget_for_sampling: null,
    max_sample_tokens: null,
    __showSettings: false
  });
  const [promptChats, setChats] = useState([{ role: 'system' }]);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const timestampParam = query.get('timestamp', '');
  const versionParam = query.get('version', '');
  const { prompt_id } = useParams();
  const [logTimestamps, setLogTimestamps] = useState([]);
  const [promptVersions, setPromptVersions] = useState([]);
  const [promptVersion, setVersion] = useState(versionParam);
  const [response, setResponse] = useState({});
  const [context, setContext] = useState('{}');
  const [timestamp, setTimestamp] = useState(timestampParam);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [responseMessages, setResponseMessages] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ws, setWs] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isFetchedModels, setIsFetchedModels] = useState(false)

  // 1. fetch all timestamps available
  const fetchTimestamps = async () => {
    const response = await axios.get(`${API_URL}/logs/timestamps?prompt_id=${prompt_id}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });

    if (response.status === 200) {
      console.log('logs_timestamps: ' + response.data)
      const log_timestamps = response.data;
      setLogTimestamps(log_timestamps.timestamps.sort((a, b) => Number(b) - Number(a)));
      if (!timestamp) {
          handleTimestampChange(log_timestamps.timestamps[0]);
      }
      else{
        fetchContext(timestamp)
      }
    }
  };

  useEffect(() => {
    if (ws) {
      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        console.log('Message from server ', message.message);
        if (message.message !== 'Endpoint request timed out') {
          setResponseMessages((prevMessages) => prevMessages + message.message);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket Disconnected');
        setWs(null);
      };

      ws.onopen = () => {
        console.log('Openned connection with API WS!');
        sendRequest();
      };
    }
  }, [ws]);

  const fetchModels = async () => {
    try {
      const modelsData = await apiFetchModels();
      if (modelsData.length === 0) {
        throw new Error('No models available');
      }
      setModels(modelsData);
      setSelectedModel(modelsData[0]); // Default to the first behavior
    } catch (error) {
      console.error(error.message || 'Error fetching models');
    }
    setIsFetchedModels(true)
  };

  const fetchPromptVersions = async () => {
    try {
      const response = await axios.get(`${API_URL}/prompts/${prompt_id}/versions`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`
        }
      });
      if (response.status === 200) {
        setPromptVersions(response.data);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };
  const fetchPromptWithVersion = async () => {
    var get_params = '';
    if (promptVersion) {
      get_params = `?version=${promptVersion}`;
    }
    const response = await axios.get(`${API_URL}/prompts/${prompt_id}${get_params}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });
    if (response.status === 200) {
      const fetchedPrompt = response.data;
      setPrompt({
        ...fetchedPrompt,
        __showSettings: false
      });
      setVersion(fetchedPrompt.version);
      setChats(fetchedPrompt.chats);
    }
  };

  const fetchPromptWithTimestamp = async () => {
    const response = await axios.get(`${API_URL}/logs/log?prompt_id=${prompt_id}&timestamp=${timestamp}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });
    if (response.status === 200) {
      const log_response = response.data;
      setPrompt({
        ...log_response.prompt,
        __showSettings: false
      });
      console.log('log_response', log_response);
      setResponse(log_response.response);
      setChats(log_response.prompt.chats);
      setTimestamp(log_response.timestamp);
    }
  };

  const [fetchingContext, setFetchingContext] = useState(false)

  // Adjusted fetchContext to accept newTimestamp parameter
  const fetchContext = async (newTimestamp = timestamp) => {
    setFetchingContext(true)
    let ts_part = '';
    if (newTimestamp) {
      ts_part = `&timestamp=${newTimestamp}`;
    }

    const response = await axios.get(`${API_URL}/logs/context?prompt_id=${prompt_id}${ts_part}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });
    if (response.status === 200) {
      setContext(JSON.stringify(response.data, null, 2));
      setFetchingContext(false)
    }
  };
  const handleTimestampChange = async (newTimestamp) => {
    if (newTimestamp !== undefined) {
      setTimestamp(newTimestamp);
      await fetchContext(newTimestamp);
    }
  };
  // 2. fetch prompt data with version or timestamp from logs
  useEffect(() => {
    const fetchLog = async () => {
      try {
        let response;
        if (!timestamp) {
          fetchPromptWithVersion();
        } else {
          fetchPromptWithTimestamp();
        }
        fetchTimestamps();
        fetchPromptVersions();
      } catch (error) {
        console.error('Error fetching prompt:', error);
      }
    };
    fetchLog();
    fetchModels();
  }, []);

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/playground', breadcrumb: 'Playground' },
    { path: window.location.href, breadcrumb: 'Test Prompt' }
  ];
  function tmsToDateTime(tms) {
    tms = parseInt(tms);
    return new Date(tms).toUTCString();
  }

  const getPromptData = () => {
    return {
      prompt_id: prompt.prompt_id,
      temperature: prompt.temperature,
      max_tokens: prompt.max_tokens,
      reserved_tokens_budget_for_sampling: prompt.reserved_tokens_budget_for_sampling,
      max_sample_tokens: prompt.max_sample_tokens,
      chats: promptChats.map((chat) => {
        return {
          role: chat.role,
          content: chat.content,
          priority: parseInt(chat.priority || '0'),
          presentation: chat.presentation,
          last_words: chat.last_words,
          is_multiple: chat.is_multiple,
          while_fits: chat.while_fits,
          continue_if_doesnt_fit: chat.continue_if_doesnt_fit,
          add_in_reverse_order: chat.add_in_reverse_order,
          in_one_message: chat.in_one_message,
          label: chat.label,
          add_if_fitted_labels: chat.add_if_fitted_labels,
          required: chat.required
        };
      })
    };
  };
  const startTestPrompt = () => {
    try {
      let token = auth.syncGetIdToken();
      const webSocketUrl = `${WSS_URL}/prod?token=${token}`;
      setResponseMessages('');
      ws && ws.close();
      setWs(new WebSocket(webSocketUrl));
    } catch {
      toast.warning('Context is empty or incorrect!', TOAST_STYLE_ERROR);
    }
  };


  const sendRequest = () => {
    const contextData = JSON.parse(context);
    const dataToSend = {
      context: contextData,
      prompt_data: {
        prompt_id,
        chats: promptChats,
        prompt_version: promptVersion
      },
      model_name: selectedModel
    };

    ws.send(JSON.stringify(dataToSend));
    setIsLoading(true);
  };

  const stopTestPrompt = () => {
    if (ws) {
      ws.close();
    }
    setIsLoading(false);
  };

  const updateChatsFromPromptManager = (newChats) => {
    setChats(newChats);
  };

  const savePrompt = async (to_publish = false) => {
    var promptData = getPromptData();
    var errors = [];
    if (promptData.prompt_id === '') {
      errors.push('Prompt id is required');
    }
    if (promptData.chats.length === 0) {
      errors.push('At least one chat is required');
    }
    if (promptData.chats.filter((chat) => !chat.content).length > 0) {
      errors.push('All chats must have content');
    }
    if (promptData.temperature && (promptData.temperature < 0 || promptData.temperature > 1)) {
      errors.push('Temperature must be between 0 and 1');
    }
    if (promptData.max_tokens && promptData.max_tokens < 1) {
      errors.push('Max tokens must be greater than 0');
    }
    if (promptData.reserved_tokens_budget_for_sampling && promptData.reserved_tokens_budget_for_sampling < 1) {
      errors.push('Reserved tokens budget for sampling must be greater than 0');
    }
    if (promptData.max_sample_tokens && promptData.max_sample_tokens < 1) {
      errors.push('Max sample tokens must be greater than 0');
    }
    if (errors.length > 0) {
      console.error('Errors:', errors);
      setAlertMessage(errors.join('; ') + '. Please fix the errors and try again.');
      setShowAlert(true);
      return;
    }
    promptData.to_publish = to_publish;
    console.log(promptData);
    const response = await axios.post(`${API_URL}/prompts`, promptData, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`,
        'Content-Type': 'application/json'
      }
    });
    if (response.status === 200) {
      toast.success('Prompt saved successfully!', TOAST_STYLE_SUCCESS);
      setVersion(response.data.version);
    } else {
      toast.error('Error saving prompt! Please try again.', TOAST_STYLE_ERROR);
      console.error('Error saving prompt:', response.data.error);
    }
  };

  return (
    <div className='flex flex-col w-full p-8 space-y-8'>
      <Breadcrumbs crumbs={crumbs} />
      <div className='grid grid-cols-8 gap-4 p-4 bg-white'>
        <div className='col-span-4'>
          <PromptManager
            _prompt={prompt}
            _chats={promptChats}
            _prompt_id={prompt_id}
            _title='Log of '
            onUpdateChats={updateChatsFromPromptManager} // Ensure this is correctly spelled
          />

          {isFetchedModels && <div className='grid grid-cols-8 gap-4 p-4 bg-white'>
            {models.length > 0 ? (
              <>
                <div className='col-span-4 overflow-auto'>
                  {/* Timestamp and other controls */}
                  <div className='mb-4'>
                    <label htmlFor='modelSelector' className='block text-sm font-medium text-gray-700'>
                      Select Model
                    </label>  
                    <select
                      id='modelSelector'
                      value={selectedModel || ''}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className='mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
                    >
                      {models.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            ) : (
              <div className='col-span-8 text-center py-4'>Please add secrets to use AI Models</div>
            )}
          </div>}
          <div className='flex justify-end gap-1'>
            <Button
              color='success'
              // disabled={isLoading}
              onClick={startTestPrompt}
            >
              Call AI Model
            </Button>
            <Button color='danger' onClick={stopTestPrompt}>
              <StopCircle />
            </Button>
            {isLoading && <Loader className='animate-spin h-5 w-5 text-blue-500' />}
          </div>
          <h2 className='col-span-8 text-2xl font-bold'>Response:</h2>
          <div className='min-w-full max-w-full max-h-3/5 p-4 border border-gray-200 rounded shadow'>
            <ReactMarkdown className='max-h-96 w-full text-wrap max-w-full overflow-y-scroll' children={responseMessages} />
          </div>
          <div className='flex justify-end mt-5 gap-1'>
            <Button color='primary' className='mt-auto' onClick={() => savePrompt()}>
              Create New Version
            </Button>
            <Button color='success' className='mt-auto' onClick={() => savePrompt(true)}>
              Create New Version & Publish
            </Button>
            <Button color='default' className='mt-auto' onClick={() => window.open(`/tests/${prompt_id}/new?timestamp=${timestamp}`)}>
              Create Test
            </Button>
          </div>
          {showAlert && (
            <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative' role='alert'>
              <strong className='font-bold'>Error! </strong>
              <span className='block sm:inline'>{alertMessage}</span>
              <span className='absolute top-0 bottom-0 right-0 px-4 py-3' onClick={() => setShowAlert(false)}>
                <X className='fill-current h-6 w-6 text-red-500' size={18} />
              </span>
            </div>
          )}
        </div>
        <div className='col-span-4 flex flex-col items-center p-4 border-l border-gray-200 overflow-auto'>
          <div className='mb-4 flex w-full justify-end'>
            <label htmlFor='timestampSelector' className='block text-sm font-medium text-gray-700 flex items-center gap-2'>
              <Calendar /> Select context
              <select
                id='timestampSelector'
                value={timestamp || ''}
                onChange={(e) => handleTimestampChange(e.target.value)}
                className='mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md'
              >
                {logTimestamps.map((ts) => (
                  <option key={ts} value={ts}>
                    {tmsToDateTime(ts)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {fetchingContext ? <Loader2 className='animate-spin' size={35} /> : <JsonEditorViewer initialJson={context} setContext={setContext} />}
        </div>
      </div>
    </div>
  );
};

export default TestPrompt;
